/**
 * Subscription Page
 * Redesigned with 3 plan modals: Free, Student, Researcher
 */

import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import {
  CheckIcon,
  CreditCardIcon,
  SparklesIcon,
  XMarkIcon,
  ArrowLeftIcon,
  CurrencyDollarIcon,
  AcademicCapIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline'
import { CheckBadgeIcon } from '@heroicons/react/24/solid'
import { motion, AnimatePresence } from 'framer-motion'
import { subscriptionsAPI, SubscriptionInfo, SubscriptionPlan } from '@api/subscriptions'
// creditsAPI removed - users can only subscribe now
import { SubscriptionBadge } from '@components/subscription/SubscriptionBadge'
import toast from 'react-hot-toast'
import clsx from 'clsx'

interface PlanModalProps {
  planKey: 'free' | 'student' | 'researcher'
  plan: SubscriptionPlan & { description?: string }
  isCurrentPlan: boolean
  isTrialEligible: boolean
  isNewUser?: boolean
  onSubscribe?: () => void
  onStartTrial?: () => void
  onContinueAsFree?: () => void
}

const PlanModal: React.FC<PlanModalProps> = ({
  planKey,
  plan,
  isCurrentPlan,
  isTrialEligible,
  isNewUser = false,
  onSubscribe,
  onStartTrial,
  onContinueAsFree,
}) => {
  const getPlanIcon = () => {
    switch (planKey) {
      case 'free':
        return <SparklesIcon className="w-8 h-8 text-slate-500" />
      case 'student':
        return <AcademicCapIcon className="w-8 h-8 text-blue-500" />
      case 'researcher':
        return <RocketLaunchIcon className="w-8 h-8 text-purple-500" />
      default:
        return <SparklesIcon className="w-8 h-8" />
    }
  }

  const getPlanColor = () => {
    switch (planKey) {
      case 'free':
        return 'border-slate-300 dark:border-slate-600'
      case 'student':
        return 'border-blue-300 dark:border-blue-600'
      case 'researcher':
        return 'border-purple-300 dark:border-purple-600'
      default:
        return 'border-slate-300'
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={clsx(
        'relative p-6 rounded-xl border-2 bg-white dark:bg-slate-800 transition-all',
        isCurrentPlan ? 'ring-2 ring-teal-500 ring-offset-2 dark:ring-offset-slate-800' : '',
        getPlanColor()
      )}
    >
      {isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full">
            Current Plan
          </span>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        {getPlanIcon()}
        <div>
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white">{plan.name}</h3>
          {plan.price_usd && (
            <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">
              ${plan.price_usd}
              <span className="text-sm font-normal text-slate-500 dark:text-slate-400">/month</span>
            </p>
          )}
        </div>
      </div>

      {plan.description && (
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">{plan.description}</p>
      )}

      <div className="space-y-2 mb-6">
        {plan.monthly_credits > 0 && (
          <div className="flex items-center gap-2">
            <CheckIcon className="w-5 h-5 text-teal-600 flex-shrink-0" />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              {plan.monthly_credits.toLocaleString()} credits/month
            </span>
          </div>
        )}
        {plan.max_documents && (
          <div className="flex items-center gap-2">
            <CheckIcon className="w-5 h-5 text-teal-600 flex-shrink-0" />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              Up to {plan.max_documents.toLocaleString()} documents
            </span>
          </div>
        )}
        {plan.max_editors && (
          <div className="flex items-center gap-2">
            <CheckIcon className="w-5 h-5 text-teal-600 flex-shrink-0" />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              Up to {plan.max_editors} editors per document
            </span>
          </div>
        )}
        {plan.latex_time_limit === null ? (
          <div className="flex items-center gap-2">
            <CheckIcon className="w-5 h-5 text-teal-600 flex-shrink-0" />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              Unlimited LaTeX compilation
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <CheckIcon className="w-5 h-5 text-teal-600 flex-shrink-0" />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              {plan.latex_time_limit}s LaTeX compilation limit
            </span>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {isCurrentPlan ? (
          <button
            disabled
            className="w-full px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded-lg font-medium cursor-not-allowed"
          >
            Current Plan
          </button>
        ) : (
          <>
            {planKey === 'free' && isNewUser && onContinueAsFree && (
              <button
                onClick={onContinueAsFree}
                className="w-full px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg"
              >
                Continue as Free User
              </button>
            )}
            {(planKey === 'student' || planKey === 'researcher') && (
              <>
                {isTrialEligible && onStartTrial && (
                  <button
                    onClick={onStartTrial}
                    className="w-full px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors mb-2"
                  >
                    Try for 30 Days
                  </button>
                )}
                {onSubscribe && (
                  <button
                    onClick={onSubscribe}
                    className="w-full px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600 rounded-lg font-medium transition-colors"
                  >
                    Subscribe
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </motion.div>
  )
}

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const isNewUser = (location.state as any)?.isNewUser || false
  
  // Handle OAuth callback
  useEffect(() => {
    const oauthSuccess = searchParams.get('oauth')
    const provider = searchParams.get('provider')
    
    if (oauthSuccess === 'success') {
      // OAuth login successful - refresh user data
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['credit-balance'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
      
      // Show success message
      
      // If new user, they're already on subscription page
      // If existing user, redirect to home after a moment
      if (!isNewUser) {
        setTimeout(() => navigate('/home', { replace: true }), 2000)
      }
    } else if (oauthSuccess === 'error') {
      const errorMessage = searchParams.get('message') || 'OAuth authentication failed'
    }
  }, [searchParams, queryClient, navigate, isNewUser])
  const [selectedPlan, setSelectedPlan] = useState<'student' | 'researcher' | null>(null)
  const [showCardForm, setShowCardForm] = useState(false)
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
  })

  const { data: subscriptionInfo, isLoading: isLoadingSubscription } = useQuery<SubscriptionInfo>({
    queryKey: ['subscription', 'info'],
    queryFn: () => subscriptionsAPI.getInfo(),
  })

  const { data: plans, isLoading: isLoadingPlans } = useQuery<Record<string, SubscriptionPlan>>({
    queryKey: ['subscription', 'plans'],
    queryFn: () => subscriptionsAPI.getPlans(),
  })

  // Credit packs removed - users can only subscribe now

  const createSubscriptionMutation = useMutation({
    mutationFn: ({ subscriptionType, cardDetails }: { subscriptionType: 'student' | 'researcher', cardDetails?: any }) =>
      subscriptionsAPI.createSubscription({
        subscription_type: subscriptionType,
        card_details: cardDetails,
        amount_paid: plans?.[subscriptionType]?.price_usd,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['credit-balance'] })
      setShowCardForm(false)
      setSelectedPlan(null)
      // Redirect new users to home after subscribing
      if (isNewUser) {
        setTimeout(() => navigate('/home', { replace: true }), 1500)
      }
    },
    onError: (error: any) => {
    },
  })

  // Credit purchase mutation removed - users can only subscribe now

  const startTrialMutation = useMutation({
    mutationFn: () => subscriptionsAPI.startTrial(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['credit-balance'] })
      // Redirect new users to home after starting trial
      if (isNewUser) {
        setTimeout(() => navigate('/home', { replace: true }), 1500)
      }
    },
    onError: (error: any) => {
    },
  })

  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  const cancelSubscriptionMutation = useMutation({
    mutationFn: () => subscriptionsAPI.cancelSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['credit-balance'] })
      setShowCancelConfirm(false)
    },
    onError: (error: any) => {
      setShowCancelConfirm(false)
    },
  })

  const handleCancelSubscription = () => {
    setShowCancelConfirm(true)
  }

  const handleSubscribe = (planType: 'student' | 'researcher') => {
    setSelectedPlan(planType)
    setShowCardForm(true)
  }

  const handleStartTrial = () => {
    startTrialMutation.mutate()
  }

  // handleBuyCredits removed - users can only subscribe now

  const handleCardSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate card details
    if (!cardDetails.cardholderName || !cardDetails.cardNumber || !cardDetails.expiryDate || !cardDetails.cvv) {
      return
    }
    
    if (selectedPlan) {
      const cardDetailsForAPI = {
        cardholder_name: cardDetails.cardholderName,
        card_number: cardDetails.cardNumber.replace(/\s/g, ''),
        expiry_date: cardDetails.expiryDate,
        cvv: cardDetails.cvv,
      }
      createSubscriptionMutation.mutate({ 
        subscriptionType: selectedPlan,
        cardDetails: cardDetailsForAPI
      })
    }
  }

  const currentSub = subscriptionInfo
  const hasActiveSubscription = currentSub && currentSub.subscription_type !== 'free' && currentSub.is_active
  const isTrialEligible = currentSub && currentSub.subscription_type === 'free' && !currentSub.trial_used && !currentSub.is_trial_active

  // Determine current plan type
  const getCurrentPlanKey = (): 'free' | 'student' | 'researcher' => {
    if (!currentSub) return 'free'
    if (currentSub.subscription_type === 'student') return 'student'
    if (currentSub.subscription_type === 'researcher') return 'researcher'
    return 'free'
  }

  const currentPlanKey = getCurrentPlanKey()

  // Define plan descriptions
  const planDescriptions: Record<string, string> = {
    free: 'Perfect for getting started. Access basic features with daily credit allocation.',
    student: 'Ideal for students and researchers. Includes monthly credits and premium features.',
    researcher: 'For professional researchers. Maximum credits, unlimited features, and priority support.',
  }

  return (
    <div className="h-full bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Banner for New Users */}
        {isNewUser && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl shadow-lg text-white"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">Welcome to SciDraft! 🎉</h2>
                <p className="text-teal-50 mb-4">
                  Your account has been created successfully. Choose how you'd like to get started:
                </p>
                <div className="flex flex-wrap gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/home', { replace: true })}
                    className="px-6 py-2 bg-white text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-colors shadow-md"
                  >
                    Continue as Free User
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      if (isTrialEligible) {
                        handleStartTrial()
                      }
                    }}
                    disabled={!isTrialEligible}
                    className="px-6 py-2 bg-white/20 text-white font-semibold rounded-lg hover:bg-white/30 transition-colors border-2 border-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Start Free Trial (30 days)
                  </motion.button>
                </div>
              </div>
              <button
                onClick={() => navigate('/home', { replace: true })}
                className="ml-4 text-white/80 hover:text-white transition-colors"
                title="Skip for now"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Header */}
        <div className="mb-8">
          {!isNewUser && (
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 mb-4 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Back</span>
            </button>
          )}
          <h1 className="text-3xl font-bold bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">
            {isNewUser ? 'Choose Your Plan' : 'Subscription Plans'}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">
            {isNewUser 
              ? 'Select a plan to get started, or continue with the free tier'
              : 'Choose the plan that best fits your needs'}
          </p>
        </div>

        {/* Current Subscription Info */}
        {currentSub && (
          <div className="mb-8 p-6 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Your Current Plan</p>
                <div className="flex items-center gap-3">
                  <SubscriptionBadge
                    subscriptionType={currentSub.subscription_type}
                    planName={currentSub.plan_name}
                    isActive={currentSub.is_active}
                    size="lg"
                  />
                  {currentSub.next_billing_date && (
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      Renews: {new Date(currentSub.next_billing_date).toLocaleDateString()}
                    </span>
                  )}
                  {currentSub.is_trial_active && currentSub.trial_end_date && (
                    <span className="text-sm text-blue-600 dark:text-blue-400">
                      Trial expires: {new Date(currentSub.trial_end_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              {hasActiveSubscription && (
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelSubscriptionMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel Subscription
                </button>
              )}
            </div>
          </div>
        )}

        {/* Plan Modals Grid */}
        {isLoadingPlans ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-slate-600 dark:text-slate-400">Loading plans...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Free Plan */}
            <PlanModal
              planKey="free"
              plan={{
                ...(plans?.free || {}),
                name: 'Free',
                description: planDescriptions.free,
                monthly_credits: 0,
                max_documents: 20,
                max_editors: 2,
                latex_time_limit: 60,
                features: {},
              }}
              isCurrentPlan={currentPlanKey === 'free'}
              isTrialEligible={false}
              isNewUser={isNewUser}
              onContinueAsFree={() => navigate('/home', { replace: true })}
            />

            {/* Student Plan */}
            <PlanModal
              planKey="student"
              plan={{
                ...(plans?.student || {
                  name: 'Student',
                  monthly_credits: 1000,
                  max_documents: 100,
                  max_editors: 5,
                  latex_time_limit: null,
                  features: {},
                  price_usd: 7.99,
                }),
                description: planDescriptions.student,
              }}
              isCurrentPlan={currentPlanKey === 'student'}
              isTrialEligible={isTrialEligible || false}
              onSubscribe={() => handleSubscribe('student')}
              onStartTrial={handleStartTrial}
            />

            {/* Researcher Plan */}
            <PlanModal
              planKey="researcher"
              plan={{
                ...(plans?.researcher || {
                  name: 'Researcher',
                  monthly_credits: 2000,
                  max_documents: 1000,
                  max_editors: 10,
                  latex_time_limit: null,
                  features: {},
                  price_usd: 14.99,
                }),
                description: planDescriptions.researcher,
              }}
              isCurrentPlan={currentPlanKey === 'researcher'}
              isTrialEligible={isTrialEligible || false}
              onSubscribe={() => handleSubscribe('researcher')}
              onStartTrial={handleStartTrial}
            />
          </div>
        )}

        {/* Card Input Form */}
        <AnimatePresence>
          {showCardForm && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <CreditCardIcon className="w-6 h-6" />
                  Payment Information
                </h3>
                <button
                  onClick={() => {
                    setShowCardForm(false)
                    setSelectedPlan(null)
                  }}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleCardSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Cardholder Name
                  </label>
                  <input
                    type="text"
                    value={cardDetails.cardholderName}
                    onChange={(e) => setCardDetails({ ...cardDetails, cardholderName: e.target.value })}
                    placeholder="John Doe"
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Card Number
                  </label>
                  <input
                    type="text"
                    value={cardDetails.cardNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\s/g, '').replace(/\D/g, '')
                      const formatted = value.match(/.{1,4}/g)?.join(' ') || value
                      setCardDetails({ ...cardDetails, cardNumber: formatted })
                    }}
                    placeholder="1234 5678 9012 3456"
                    maxLength={19}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Expiry Date (MM/YY)
                    </label>
                    <input
                      type="text"
                      value={cardDetails.expiryDate}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '').slice(0, 4)
                        if (value.length >= 2) {
                          value = value.slice(0, 2) + '/' + value.slice(2)
                        }
                        setCardDetails({ ...cardDetails, expiryDate: value })
                      }}
                      placeholder="MM/YY"
                      maxLength={5}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">CVV</label>
                    <input
                      type="text"
                      value={cardDetails.cvv}
                      onChange={(e) => setCardDetails({ ...cardDetails, cvv: e.target.value.replace(/\D/g, '').slice(0, 3) })}
                      placeholder="123"
                      maxLength={3}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                </div>

                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Note:</strong> Payment is currently simulated. No actual charges will be made.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={createSubscriptionMutation.isPending}
                  className="w-full px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createSubscriptionMutation.isPending
                    ? 'Processing...'
                    : 'Subscribe'}
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Cancel Subscription Confirmation Modal */}
        <AnimatePresence>
          {showCancelConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowCancelConfirm(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                    Cancel Subscription?
                  </h3>
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                <p className="text-slate-600 dark:text-slate-400 mb-6">
                  Are you sure you want to cancel your subscription? You will lose access to premium features
                  and your monthly credits. Your subscription will remain active until the end of your current billing period.
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    className="flex-1 px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                  >
                    Keep Subscription
                  </button>
                  <button
                    onClick={() => cancelSubscriptionMutation.mutate()}
                    disabled={cancelSubscriptionMutation.isPending}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {cancelSubscriptionMutation.isPending ? 'Cancelling...' : 'Yes, Cancel'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default SubscriptionPage
